import React from 'react';
import { Info } from 'lucide-react';
import { usePWAInstall } from '../lib/usePWAInstall';

/**
 * DriverInstallTip — first-visit guidance for Android drivers.
 *
 * Background: Chrome on Android enforces one PWA per overlapping
 * scope per origin. The storefront's scope `/` claims `/driver` too,
 * so installing the customer storefront FIRST blocks the driver
 * portal install with "Already installed". The reverse order works
 * (driver scope `/driver` doesn't claim `/`).
 *
 * This tip is shown on the Driver login page when:
 *  - Platform looks like Android-Chrome (browser install prompt is
 *    available — `canInstall` is truthy), AND
 *  - Storefront PWA is NOT yet installed on this device (no
 *    `mr_pwa_installed` flag), AND
 *  - Driver portal itself is not yet installed (`installed` is
 *    false — we don't badger users who already have it set up).
 *
 * Hidden on iOS (no conflict), hidden on desktop (no relevance),
 * hidden once either app is installed (the moment is past).
 */
const DriverInstallTip = () => {
  const { canInstall, installed, platform } = usePWAInstall();

  // Only show on Android-Chrome-style installable contexts.
  if (platform === 'ios' || installed || !canInstall) return null;

  // If the storefront PWA is ALREADY installed, the conflict-warning
  // inside PWAInstallPrompt handles it — no need to also show a "first
  // install this" tip.
  const storefrontInstalled =
    typeof window !== 'undefined' &&
    localStorage.getItem('mr_pwa_installed') === '1';
  if (storefrontInstalled) return null;

  return (
    <div
      className="mb-4 rounded-2xl border border-emerald-400/30 bg-emerald-500/10 px-4 py-3 flex items-start gap-2.5"
      data-testid="driver-install-first-tip"
    >
      <Info className="w-4 h-4 text-emerald-300 flex-shrink-0 mt-0.5" />
      <div className="text-[11px] text-emerald-100 leading-snug">
        <span className="font-bold">Tip for Android: install this Driver Portal first</span> before the customer store. Chrome only allows one home-screen icon per site on Android — installing the driver app first keeps both available.
      </div>
    </div>
  );
};

export default DriverInstallTip;
